import {
	deleteLocation,
	getLocation,
	getLocationGeoLocation,
	updateLocation,
} from "@/services/location-service";
import { useUserStore } from "@/stores/user-store";
import {
	ArrowLeftOutlined,
	DeleteOutlined,
	EnvironmentOutlined,
	ExclamationCircleOutlined,
	HomeOutlined,
	InfoCircleOutlined,
	ReloadOutlined,
	SaveOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import { useJsApiLoader } from "@react-google-maps/api";
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Space,
	Spin,
	Switch,
	Typography,
	message,
} from "antd";
import type { InputRef } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Google Maps libraries for autocomplete (must match other components)
const libraries: ("places" | "visualization" | "geometry")[] = [
	"places",
	"visualization",
	"geometry",
];

interface LocationData {
	id: number;
	organization_id: number;
	name?: string;
	store_number?: string;
	address1: string;
	address2?: string;
	city?: string;
	state?: string;
	zip?: string;
	country?: string;
	google_address?: string;
	latitude?: number;
	longitude?: number;
	is_active: boolean;
	is_test_location: boolean;
	is_demo_location: boolean;
	matterport?: string;
	iguide?: string;
	square_footage?: number;
	brand?: string;
	county?: string;
	zip_suffix?: string;
	unique_fields?: any;
}

const LocationSettings: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useUserStore();
	const [form] = Form.useForm();

	// State management
	const [locationData, setLocationData] = useState<LocationData | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [fetchingGPS, setFetchingGPS] = useState(false);
	const [autocomplete, setAutocomplete] =
		useState<google.maps.places.Autocomplete | null>(null);

	// Refs
	const autocompleteRef = useRef<InputRef>(null);

	// Google Maps API
	const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
		libraries: libraries,
	});

	// Load location data
	useEffect(() => {
		const fetchLocationData = async () => {
			if (!id) {
				message.error("Location ID is required");
				navigate("/locations");
				return;
			}

			try {
				setLoading(true);
				const data = await getLocation(id);
				setLocationData(data);

				// Set form values
				form.setFieldsValue({
					name: data.name || "",
					store_number: data.store_number || "",
					address1: data.address1 || "",
					address2: data.address2 || "",
					city: data.city || "",
					state: data.state || "",
					zip: data.zip || "",
					country: data.country || "US",
					google_address: data.google_address || "",
					latitude: data.latitude || null,
					longitude: data.longitude || null,
					is_active: data.is_active,
					is_test_location: data.is_test_location,
					is_demo_location: data.is_demo_location,
					matterport: data.matterport || "",
					iguide: data.iguide || "",
					square_footage: data.square_footage || null,
					brand: data.brand || "",
					county: data.county || "",
					zip_suffix: data.zip_suffix || "",
				});
			} catch (error) {
				console.error("Error fetching location data:", error);
				message.error("Failed to load location data");
				navigate("/locations");
			} finally {
				setLoading(false);
			}
		};

		fetchLocationData();
	}, [id, form, navigate]);

	// Initialize Google Places Autocomplete
	useEffect(() => {
		if (isGoogleMapsLoaded && autocompleteRef.current?.input && !autocomplete) {
			const autocompleteInstance = new window.google.maps.places.Autocomplete(
				autocompleteRef.current.input,
				{
					types: ["address"],
					componentRestrictions: { country: ["us", "ca"] },
				},
			);

			autocompleteInstance.addListener("place_changed", () => {
				const place = autocompleteInstance.getPlace();
				if (place.geometry && place.geometry.location) {
					const addressComponents = place.address_components || [];

					// Parse address components
					let streetNumber = "";
					let route = "";
					let city = "";
					let state = "";
					let zip = "";
					let country = "";

					addressComponents.forEach((component) => {
						const types = component.types;
						if (types.includes("street_number")) {
							streetNumber = component.long_name;
						} else if (types.includes("route")) {
							route = component.long_name;
						} else if (types.includes("locality")) {
							city = component.long_name;
						} else if (types.includes("administrative_area_level_1")) {
							state = component.short_name;
						} else if (types.includes("postal_code")) {
							zip = component.long_name;
						} else if (types.includes("country")) {
							country = component.short_name;
						}
					});

					// Update form fields
					form.setFieldsValue({
						address1: `${streetNumber} ${route}`.trim(),
						city: city,
						state: state,
						zip: zip,
						country: country,
						google_address: place.formatted_address,
						latitude: place.geometry.location.lat(),
						longitude: place.geometry.location.lng(),
					});

					setHasChanges(true);
				}
			});

			setAutocomplete(autocompleteInstance);
		}

		return () => {
			if (autocomplete) {
				window.google.maps.event.clearInstanceListeners(autocomplete);
			}
		};
	}, [isGoogleMapsLoaded, autocomplete, form]);

	// Handle form changes
	const handleFormChange = () => {
		setHasChanges(true);
	};

	// Handle form submission
	const handleSubmit = async (values: any) => {
		if (!locationData) return;

		try {
			setSaving(true);

			// Prepare update data
			const updateData = {
				name: values.name || null,
				store_number: values.store_number || null,
				address1: values.address1,
				address2: values.address2 || null,
				city: values.city,
				state: values.state,
				zip: values.zip,
				country: values.country || "US",
				google_address: values.google_address || null,
				latitude: values.latitude || null,
				longitude: values.longitude || null,
				is_active: values.is_active,
				matterport: values.matterport || null,
				iguide: values.iguide || null,
			};

			await updateLocation(locationData.id, false, updateData);

			message.success("Location updated successfully");
			setHasChanges(false);

			// Refresh location data
			const updatedData = await getLocation(locationData.id.toString());
			setLocationData(updatedData);
			
			// Update form with refreshed data to ensure sync
			form.setFieldsValue({
				name: updatedData.name || "",
				store_number: updatedData.store_number || "",
				address1: updatedData.address1 || "",
				address2: updatedData.address2 || "",
				city: updatedData.city || "",
				state: updatedData.state || "",
				zip: updatedData.zip || "",
				country: updatedData.country || "US",
				google_address: updatedData.google_address || "",
				latitude: updatedData.latitude || null,
				longitude: updatedData.longitude || null,
				is_active: updatedData.is_active,
				is_test_location: updatedData.is_test_location,
				is_demo_location: updatedData.is_demo_location,
				matterport: updatedData.matterport || "",
				iguide: updatedData.iguide || "",
				square_footage: updatedData.square_footage || null,
				brand: updatedData.brand || "",
				county: updatedData.county || "",
				zip_suffix: updatedData.zip_suffix || "",
			});
		} catch (error) {
			console.error("Error updating location:", error);
			message.error("Failed to update location");
		} finally {
			setSaving(false);
		}
	};

	// Handle reset form
	const handleReset = () => {
		if (!locationData) return;

		form.setFieldsValue({
			name: locationData.name || "",
			store_number: locationData.store_number || "",
			address1: locationData.address1 || "",
			address2: locationData.address2 || "",
			city: locationData.city || "",
			state: locationData.state || "",
			zip: locationData.zip || "",
			country: locationData.country || "US",
			google_address: locationData.google_address || "",
			latitude: locationData.latitude || null,
			longitude: locationData.longitude || null,
			is_active: locationData.is_active,
			is_test_location: locationData.is_test_location,
			is_demo_location: locationData.is_demo_location,
			matterport: locationData.matterport || "",
			iguide: locationData.iguide || "",
			square_footage: locationData.square_footage || null,
			brand: locationData.brand || "",
			county: locationData.county || "",
			zip_suffix: locationData.zip_suffix || "",
		});

		setHasChanges(false);
	};

	// Handle getting GPS coordinates from address
	const handleGetGPSFromAddress = async () => {
		try {
			setFetchingGPS(true);

			// Get current form values
			const values = form.getFieldsValue();
			
			// Build address string from form fields
			const addressParts = [
				values.address1,
				values.address2,
				values.city,
				values.state,
				values.zip,
				values.country || "US"
			].filter(Boolean); // Remove empty values

			if (!values.address1 || !values.city || !values.state) {
				message.warning("Please fill in at least address, city, and state to fetch GPS coordinates");
				return;
			}

			const addressString = addressParts.join(", ");
			console.log("Fetching GPS for address:", addressString);

			// Call Google Maps Geocoding API
			const geocodeResponse = await getLocationGeoLocation({
				google_address: addressString
			});

			console.log("Geocoding response:", geocodeResponse);

			// Check if we got valid results
			if (!geocodeResponse.results || geocodeResponse.results.length === 0) {
				message.error("Could not find GPS coordinates for this address. Please verify the address is correct.");
				return;
			}

			const location = geocodeResponse.results[0].geometry.location;
			const { lat, lng } = location;

			// Update form fields
			form.setFieldsValue({
				latitude: lat,
				longitude: lng,
				google_address: geocodeResponse.results[0].formatted_address
			});

			setHasChanges(true);
			message.success(`GPS coordinates updated: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);

		} catch (error) {
			console.error("Error fetching GPS coordinates:", error);
			message.error("Failed to fetch GPS coordinates. Please check the address and try again.");
		} finally {
			setFetchingGPS(false);
		}
	};

	// Handle delete location
	const handleDeleteLocation = () => {
		if (!locationData) return;

		Modal.confirm({
			title: "Delete Location",
			icon: <ExclamationCircleOutlined />,
			content: (
				<div>
					<p>Are you sure you want to delete this location?</p>
					<p>
						<strong>This action cannot be undone.</strong>
					</p>
					<p style={{ marginTop: "12px", fontSize: "12px", color: "#888" }}>
						Note: This location can only be deleted if it has zero submissions
						and zero assets.
					</p>
				</div>
			),
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					setDeleting(true);
					const result = await deleteLocation(locationData.id);

					if (result.success) {
						message.success(result.message);
						// Navigate back to locations list
						navigate("/locations");
					} else {
						// Show error message from backend
						Modal.error({
							title: "Cannot Delete Location",
							content: result.message,
						});
					}
				} catch (error: any) {
					console.error("Error deleting location:", error);
					message.error(error.message || "Failed to delete location");
				} finally {
					setDeleting(false);
				}
			},
		});
	};

	if (loading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}
			>
				<Spin size="large" />
			</div>
		);
	}

	if (!locationData) {
		return (
			<Alert
				message="Location Not Found"
				description="The requested location could not be found."
				type="error"
				showIcon
			/>
		);
	}

	return (
		<div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
			<div style={{ marginBottom: "24px" }}>
				<Space direction="vertical" size="small" style={{ width: "100%" }}>
					<Button
						type="link"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(`/locations/${id}`)}
						size="large"
						className="px-0"
						style={{ marginBottom: "8px" }}
					>
						Back to Location
					</Button>
					<Title level={2} style={{ margin: 0 }}>
						<SettingOutlined style={{ marginRight: "8px" }} />
						Location Settings
					</Title>
					<Text type="secondary">
						Manage location details, address information, and configuration
						settings
					</Text>
				</Space>
			</div>

			{hasChanges && (
				<Alert
					message="Unsaved Changes"
					description="You have unsaved changes. Don't forget to save your updates."
					type="warning"
					showIcon
					style={{ marginBottom: "24px" }}
				/>
			)}

			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				onValuesChange={handleFormChange}
				size="large"
			>
				{/* Basic Information */}
				<Card
					title={
						<>
							<HomeOutlined style={{ marginRight: "8px" }} />
							Basic Information
						</>
					}
					style={{ marginBottom: "24px" }}
				>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Location Name"
								name="name"
								tooltip="Display name for this location"
							>
								<Input placeholder="Enter location name" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Store Number"
								name="store_number"
								tooltip="Unique identifier for this location"
							>
								<Input placeholder="Enter store number" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Brand"
								name="brand"
								tooltip="Brand or franchise name"
							>
								<Input placeholder="Enter brand name" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Square Footage"
								name="square_footage"
								tooltip="Total square footage of the facility"
							>
								<InputNumber
									placeholder="Enter square footage"
									style={{ width: "100%" }}
									min={0}
									formatter={(value) =>
										`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
									}
									parser={(value) => value!.replace(/\$\s?|(,*)/g, "") as any}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>

				{/* Address Information */}
				<Card
					title={
						<>
							<EnvironmentOutlined style={{ marginRight: "8px" }} />
							Address Information
						</>
					}
					style={{ marginBottom: "24px" }}
				>
					<Row gutter={[16, 16]}>
						<Col xs={24}>
							<Form.Item
								label="Address Line 1"
								name="address1"
								rules={[{ required: true, message: "Address is required" }]}
								tooltip="Primary street address"
							>
								<Input
									ref={autocompleteRef}
									placeholder="Enter street address"
								/>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Form.Item
								label="Address Line 2"
								name="address2"
								tooltip="Suite, unit, or additional address information"
							>
								<Input placeholder="Suite, unit, etc. (optional)" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="City"
								name="city"
								rules={[{ required: true, message: "City is required" }]}
							>
								<Input placeholder="Enter city" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="State"
								name="state"
								rules={[{ required: true, message: "State is required" }]}
							>
								<Input placeholder="Enter state" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="ZIP Code"
								name="zip"
								rules={[{ required: true, message: "ZIP code is required" }]}
							>
								<Input placeholder="Enter ZIP code" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Country"
								name="country"
								tooltip="Country code (e.g., US, CA)"
							>
								<Select placeholder="Select country">
									<Option value="US">United States</Option>
									<Option value="CA">Canada</Option>
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="County"
								name="county"
								tooltip="County or parish name"
							>
								<Input placeholder="Enter county" />
							</Form.Item>
						</Col>
					</Row>

					<Divider />

					<Row gutter={[16, 16]}>
						<Col xs={24}>
							<Form.Item
								label="Google Address"
								name="google_address"
								tooltip="Full formatted address from Google Maps"
							>
								<TextArea
									rows={2}
									placeholder="Google Maps formatted address"
									disabled
								/>
							</Form.Item>
						</Col>
						<Col xs={24}>
							<Alert
								type="info"
								message="GPS Coordinates"
								description={
									<div>
										<p style={{ marginBottom: "12px" }}>
											GPS coordinates are required for Visual Tag 2.0 enrollment. 
											Click the button below to automatically fetch them from the address above.
										</p>
										<Button
											type="primary"
											icon={<EnvironmentOutlined />}
											loading={fetchingGPS}
											onClick={handleGetGPSFromAddress}
										>
											Get GPS from Address
										</Button>
									</div>
								}
								style={{ marginBottom: "16px" }}
							/>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Latitude"
								name="latitude"
								tooltip="GPS latitude coordinate (required for Visual Tag 2.0)"
							>
								<InputNumber
									placeholder="Enter latitude"
									style={{ width: "100%" }}
									step={0.000001}
									precision={6}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Longitude"
								name="longitude"
								tooltip="GPS longitude coordinate (required for Visual Tag 2.0)"
							>
								<InputNumber
									placeholder="Enter longitude"
									style={{ width: "100%" }}
									step={0.000001}
									precision={6}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>

				{/* Virtual Tours */}
				<Card
					title={
						<>
							<InfoCircleOutlined style={{ marginRight: "8px" }} />
							Virtual Tours
						</>
					}
					style={{ marginBottom: "24px" }}
				>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12}>
							<Form.Item
								label="Matterport URL"
								name="matterport"
								tooltip="Matterport virtual tour URL or ID"
							>
								<Input placeholder="Enter Matterport URL" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={12}>
							<Form.Item
								label="iGuide URL"
								name="iguide"
								tooltip="iGuide virtual tour URL or ID"
							>
								<Input placeholder="Enter iGuide URL" />
							</Form.Item>
						</Col>
					</Row>
				</Card>

				{/* Status Settings */}
				<Card
					title={
						<>
							<SettingOutlined style={{ marginRight: "8px" }} />
							Status Settings
						</>
					}
					style={{ marginBottom: "24px" }}
				>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Active Location"
								name="is_active"
								valuePropName="checked"
								tooltip="Whether this location is currently active"
							>
								<Switch checkedChildren="Active" unCheckedChildren="Inactive" />
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Test Location"
								name="is_test_location"
								valuePropName="checked"
								tooltip="Mark as test location for development purposes"
							>
								<Switch
									checkedChildren="Test"
									unCheckedChildren="Production"
									disabled={!user?.role || user.role.access_level < 500}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							<Form.Item
								label="Demo Location"
								name="is_demo_location"
								valuePropName="checked"
								tooltip="Mark as demo location for demonstrations"
							>
								<Switch
									checkedChildren="Demo"
									unCheckedChildren="Regular"
									disabled={!user?.role || user.role.access_level < 500}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Card>

				{/* Action Buttons */}
				<Card>
					<Space size="middle">
						<Button
							type="primary"
							htmlType="submit"
							icon={<SaveOutlined />}
							loading={saving}
							disabled={!hasChanges}
						>
							Save Changes
						</Button>
						<Button
							icon={<ReloadOutlined />}
							onClick={handleReset}
							disabled={!hasChanges}
						>
							Reset
						</Button>
						<Button onClick={() => navigate(`/locations/${id}`)}>
							Back to Location
						</Button>
					</Space>
				</Card>

				{/* Danger Zone */}
				<Card
					title={
						<span style={{ color: "#ff4d4f" }}>
							<ExclamationCircleOutlined style={{ marginRight: "8px" }} />
							Danger Zone
						</span>
					}
					style={{ marginTop: "24px", borderColor: "#ff4d4f" }}
				>
					<Alert
						message="Delete Location"
						description="Permanently delete this location. This action cannot be undone. The location can only be deleted if it has zero submissions and zero assets."
						type="error"
						showIcon
						style={{ marginBottom: "16px" }}
					/>
					<Button
						danger
						icon={<DeleteOutlined />}
						onClick={handleDeleteLocation}
						loading={deleting}
					>
						Delete Location
					</Button>
				</Card>
			</Form>
		</div>
	);
};

export default LocationSettings;
